import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Compiler,
	Component,
	ComponentFactory,
	ComponentFactoryResolver,
	ComponentRef,
	EventEmitter,
	Injector,
	Input,
	NgModuleFactory,
	NgModuleRef,
	Output,
	Type,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from "@angular/core";

import {
	LazyComponent,
	LazyItem,
	LazyItemExtraConfig,
	LazyItemType,
	LazyLoaderModuleRef,
} from "../sdk/lazy-loader.model";

@Component({
	selector: "obg-lazy-loader",
	templateUrl: "./lazy-loader.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LazyLoaderContainer implements AfterViewInit {
	@Input() lazyItem: LazyItem | undefined;

	@Output() componentRef = new EventEmitter<ComponentRef<Component>>();

	@ViewChild("lazyTemplate", { read: ViewContainerRef }) lazyTemplate!: ViewContainerRef;

	constructor(
		private compiler: Compiler,
		private injector: Injector,
		private changeDetector: ChangeDetectorRef,
		private componentFactoryResolver: ComponentFactoryResolver
	) {}

	async ngAfterViewInit() {
		if (!this.lazyItem) {
			return;
		}

		this.lazyItem.type ??= LazyItemType.component;
		const { name, path, moduleData } = this.lazyItem;

		this.lazyItem.type === LazyItemType.component
			? this.lazyLoadComponent(name, path, moduleData)
			: this.lazyLoadModule(name, path, moduleData);
	}

	private lazyLoadModule(
		moduleName: string,
		path: () => Promise<{ [key: string]: Type<LazyLoaderModuleRef> }>,
		data?: LazyItemExtraConfig
	) {
		path().then(async (payload) => {
			const moduleFactory = (await this.compiler.compileModuleAsync(
				payload[moduleName]
			)) as NgModuleFactory<LazyLoaderModuleRef>;
			const moduleRef: NgModuleRef<LazyLoaderModuleRef> = moduleFactory.create(this.injector);
			const componentFactory = moduleRef.instance.getComponent();
			this.createComponent(componentFactory, data);
		});
	}

	private lazyLoadComponent(
		componentName: string,
		path: () => Promise<{ [key: string]: Type<LazyComponent> }>,
		data?: LazyItemExtraConfig
	) {
		path().then(async (payload) => {
			const componentFactory = this.componentFactoryResolver.resolveComponentFactory<LazyComponent>(
				payload[componentName]
			);
			this.createComponent(componentFactory, data);
		});
	}

	private createComponent(componentFactory: ComponentFactory<LazyComponent>, data?: LazyItemExtraConfig) {
		this.lazyTemplate.clear();
		const componentRef = this.lazyTemplate.createComponent(componentFactory);
		if (data) {
			componentRef.instance.data = {
				...componentRef.instance.data,
				moduleData: data,
			};
		}
		this.changeDetector.detectChanges();
		this.componentRef.emit(componentRef);
	}
}
